import React, { useState } from 'react';
import BRAND from '../brand';
import { getPlatform, openLink } from '../platform';
import { useLegalLinks } from '../legal';

// Ссылка на юр. документ есть только если задана в админ-настройках (или в паке);
// '#' — фолбэк пака «документа ещё нет», такую ссылку не показываем
const isRealUrl = (url) => !!url && url !== '#';
// Ссылки на документы: цвет текста + подчёркивание. Не .contact-link — он красит в
// --primary-color, а у Стемса это светло-жёлтый на серой карточке (не читается)
const legalLinkStyle = { color: 'inherit', textDecoration: 'underline' };

// Раскрывающаяся секция (аккордеон)
function Accordion({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className={`accordion ${isOpen ? 'open' : ''}`}>
      <button className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <span className="accordion-icon">{isOpen ? '▼' : '▶'}</span>
        <span className="accordion-title">{title}</span>
      </button>
      {isOpen && (
        <div className="accordion-content">
          {children}
        </div>
      )}
    </div>
  );
}

function AboutPage() {
  // Оферта и политика ПДн — из админ-настроек (GET /api/settings), как в оформлении заказа.
  // Постоянная ссылка на политику обязательна по ч. 2 ст. 18.1 152-ФЗ; «О проекте» —
  // единственный экран, доступный из нижнего меню всегда (решение 2026-09-10).
  const legal = useLegalLinks();
  const hasPolicy = isRealUrl(legal.policyUrl);
  const hasOferta = isRealUrl(legal.ofertaUrl);
  return (
    <div className="about-page">
      {/* Hero секция с STEMS */}
      <div className="about-hero">
        <div className="about-hero-bg"></div>
        {BRAND.logo.type === 'image' ? (
          <img
            src={BRAND.logo.src}
            alt={BRAND.name}
            style={{ height: 48, maxWidth: '80%', display: 'block', margin: '0 auto', position: 'relative' }}
          />
        ) : (
          <h1 className="about-hero-title">{BRAND.name}</h1>
        )}
        {BRAND.tagline && <p className="about-hero-subtitle">{BRAND.tagline}</p>}
        {BRAND.about.heroEmoji && <div className="about-hero-flowers">{BRAND.about.heroEmoji}</div>}
      </div>

      {/* Философия Бренда */}
      <div className="about-card">
        <h2 className="about-title">Философия бренда</h2>
        <p className="about-text">
          <strong>{BRAND.name}</strong> — {BRAND.about.philosophyIntro}
        </p>
        {BRAND.about.philosophyLines.map((line) => (
          <p className="about-text" key={line}>{line}</p>
        ))}
      </div>

      {/* Преимущества */}
      <div className="about-section">
        <h3 className="section-title">Преимущества</h3>
        
        {BRAND.about.advantages.map((adv, i) => (
          <div className="advantage-item" key={i}>
            <div className={`advantage-icon advantage-icon-${adv.color}`}>{adv.icon}</div>
            <span className="advantage-label">
              {adv.label || <>Кешбэк на счёт {BRAND.name} с каждого заказа</>}
            </span>
          </div>
        ))}
      </div>

      {/* Что включает сервисный сбор */}
      <div className="about-section">
        <h3 className="section-title">Что входит в сервисный сбор:</h3>
        
        {BRAND.about.serviceFeeItems.map((item, i) => (
          <div className="advantage-item" key={i}>
            <div className="advantage-icon advantage-icon-green">{item.icon}</div>
            <span className="advantage-label">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Раскрывающиеся секции */}
      <div className="about-section accordions-section">
        <Accordion title="Как продлить жизнь цветам">
          <ul className="accordion-list-bullet">
            {BRAND.about.careTips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </Accordion>

        <Accordion title="Доставка и способы оплаты">
          <ul className="accordion-list-bullet">
            {BRAND.about.deliveryLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Accordion>

        <Accordion title="Политика обработки данных">
          <p className="accordion-text">
            Мы уважаем вашу конфиденциальность и заботимся о защите персональных данных.
          </p>
          <p className="accordion-text">
            При оформлении заказа вы предоставляете только необходимую информацию: имя, телефон, адрес доставки и способ оплаты.
          </p>
          <p className="accordion-text">
            Эти данные используются исключительно для оформления и доставки заказа.
          </p>
          <p className="accordion-text">
            Мы не передаём ваши данные третьим лицам, кроме партнёров, участвующих в доставке (курьеры, платёжные сервисы).
          </p>
          <p className="accordion-text">
            Все персональные данные обрабатываются в соответствии с Федеральным законом № 152-ФЗ «О персональных данных».
          </p>
          <p className="accordion-text">
            Если вы хотите удалить или изменить свои данные — просто напишите нам, и мы всё сделаем.
          </p>
          {hasPolicy && (
            <p className="accordion-text">
              Полный текст:{' '}
              {/* Обычная ссылка, как в CheckoutModal — поведение юр. документов едино на всех экранах */}
              <a href={legal.policyUrl} target="_blank" rel="noopener noreferrer" style={legalLinkStyle}>
                Политика конфиденциальности
              </a>
            </p>
          )}
        </Accordion>
      </div>

      {/* Контакты */}
      <div className="about-section contacts-section">
        <h3 className="section-title">Контакты</h3>
        
        <div className="contact-row">
          <span className="contact-label">Название организации:</span>
          <span className="contact-value">{BRAND.about.contacts.org}</span>
        </div>
        
        <div className="contact-row">
          <span className="contact-label">ИНН:</span>
          <span className="contact-value">{BRAND.about.contacts.inn}</span>
        </div>
        
        {/* ИП → ОГРНИП, юрлицо → ОГРН: пак задаёт один из ключей */}
        <div className="contact-row">
          <span className="contact-label">{BRAND.about.contacts.ogrn ? 'ОГРН:' : 'ОГРНИП:'}</span>
          <span className="contact-value">{BRAND.about.contacts.ogrn || BRAND.about.contacts.ogrnip}</span>
        </div>

        {BRAND.about.contacts.address && (
          <div className="contact-row">
            <span className="contact-label">Адрес:</span>
            <span className="contact-value">{BRAND.about.contacts.address}</span>
          </div>
        )}

        <div className="contact-row">
          <span className="contact-label">Телефон:</span>
          <span className="contact-value">
            <a href={BRAND.about.contacts.phoneHref} style={{ color: 'inherit', textDecoration: 'none' }}>
              {BRAND.about.contacts.phone}
            </a>
          </span>
        </div>

        {BRAND.about.contacts.email && (
          <div className="contact-row">
            <span className="contact-label">E-mail:</span>
            <span className="contact-value">
              <a href={`mailto:${BRAND.about.contacts.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                {BRAND.about.contacts.email}
              </a>
            </span>
          </div>
        )}

        {BRAND.about.contacts.support && (
          <div className="contact-row">
            <span className="contact-label">Поддержка:</span>
            <span className="contact-value">
              {BRAND.supportUrl ? (
                <a
                  href={BRAND.supportUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  onClick={(e) => {
                    // Как у ссылки на платформу ниже: в мини-аппе — через SDK, в вебе — обычная ссылка
                    if (getPlatform() !== 'web') {
                      e.preventDefault();
                      openLink(BRAND.supportUrl);
                    }
                  }}
                >
                  {BRAND.about.contacts.support}
                </a>
              ) : BRAND.about.contacts.support}
            </span>
          </div>
        )}

        {(hasOferta || hasPolicy) && (
          <div className="contact-row">
            <span className="contact-label">Документы:</span>
            <span className="contact-value">
              {hasOferta && (
                <a href={legal.ofertaUrl} target="_blank" rel="noopener noreferrer" style={legalLinkStyle}>
                  Публичная оферта
                </a>
              )}
              {hasOferta && hasPolicy && ' · '}
              {hasPolicy && (
                <a href={legal.policyUrl} target="_blank" rel="noopener noreferrer" style={legalLinkStyle}>
                  Политика конфиденциальности
                </a>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Платформа. Скрывается через brand.poweredBy = null (белый лейбл). */}
      {BRAND.poweredBy && (
        <p className="powered-by">
          {BRAND.poweredBy.text}
          <br />
          <a
            href={BRAND.poweredBy.url}
            rel="noopener nofollow"
            target="_blank"
            onClick={(e) => {
              // Telegram и Max: обычный переход увёл бы покупателя из витрины
              // магазина — открываем во внешнем браузере. В вебе — обычная ссылка.
              if (getPlatform() !== 'web') {
                e.preventDefault();
                openLink(BRAND.poweredBy.url);
              }
            }}
          >
            {BRAND.poweredBy.linkLabel}
          </a>
        </p>
      )}
    </div>
  );
}

export default AboutPage;
